//mushroom-board-page.tsx

import Button from "@mui/material/Button";
import React, { useEffect, useState, useContext } from "react";
import {
  GridColDef,
  GridRowSelectionModel,
  GridPaginationModel,
  GridToolbar,
  GridRowParams,
  GridRowClassNameParams,
  useGridApiRef,
  GridDensity,
  GridInitialState,
  GRID_CHECKBOX_SELECTION_COL_DEF,
  GridRowId,
} from "@mui/x-data-grid";
import {
  IconButton,
  CircularProgress,
  Box,
  TextField,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import LoadingBackdrop from "../components/loading-backdrop";
import Page from "../components/page";
import { StripedDataGrid } from "../components/striped-data-grid";
import { DateTime } from "luxon";
import Inventory2OutlinedIcon from "@mui/icons-material/Inventory2Outlined";
import { ActiveFilterControls } from "../components/active-filter-controls";
import { getSearchParamNumber } from "../utils/history";
import { useHistory } from "react-router-dom";
import { ErrorContext } from "../components/app-routes";

type MushroomStage =
  | "agar"
  | "liquid_culture"
  | "grain_spawn"
  | "bulk_spawn"
  | "fruiting_block";

type MushroomStatus =
  | "incubating"
  | "casing"
  | "fruiting"
  | "cold_hold"
  | "harvest"
  | "completed";

interface MushroomBoardItem {
  id: number;
  code: string;
  strainName: string;
  stage: MushroomStage;
  status: MushroomStatus;
  roomName: string | null;
  inoculatedAt: string;
  notes: string | null;
  archivedAt: string | null;
  productionUnitId: number | null;
  productionUnitName: string | null;
  unitCount: number | null;
}

const boardGridStateStorageKey = "mushroomBoardGridState";
const boardGridDensityStorageKey = "mushroomBoardGridDensity";
const mushroomBoardStorageKey = "mushroomBoardItems";
const mushroomStrainsStorageKey = "mushroomStrains";
const mushroomRoomsStorageKey = "mushroomRooms";
const mushroomProductionUnitsStorageKey = "mushroomProductionUnits";

type SimpleOption = { name: string };
type StageValue = MushroomStage | "";
type StatusValue = MushroomStatus | "";
type ProductionUnitOption = {
  id: number;
  name: string;
  wetWeight?: number | null;
  wetWeightUom?: string | null;
};

const defaultProductionUnits: ProductionUnitOption[] = [
  { id: 1, name: "10 lb Bag", wetWeight: 10, wetWeightUom: "lb" },
  { id: 2, name: "1020 Tray" },
];

const defaultStrains: SimpleOption[] = [
  { name: "Blue Oyster" },
  { name: "Lion’s Mane" },
];

const defaultRooms: SimpleOption[] = [
  { name: "Incubation Shelf 1" },
  { name: "Fruiting Room A" },
];

function loadOptionsWithFallback(
  storageKey: string,
  fallback: SimpleOption[],
): SimpleOption[] {
  const raw = window.localStorage.getItem(storageKey);

  if (!raw) return fallback;

  try {
    const parsed = JSON.parse(raw);
    const list = Array.isArray(parsed) ? parsed : [];

    const normalized = list
      .map((x: any) => ({ name: (x?.name ?? x?.Name ?? "").trim() }))
      .filter((x: any) => !!x.name);

    return normalized.length ? normalized : fallback;
  } catch {
    window.localStorage.removeItem(storageKey);
    return fallback;
  }
}

function loadStrainsWithFallback(
  storageKey: string,
  fallback: SimpleOption[],
): any[] {
  const raw = window.localStorage.getItem(storageKey);

  if (!raw) return fallback;

  try {
    const parsed = JSON.parse(raw);
    const list = Array.isArray(parsed) ? parsed : [];

    const normalized = list
      .map((x: any) => ({ ...x, name: (x?.name ?? x?.Name ?? "").trim() }))
      .filter((x: any) => !!x.name);

    return normalized.length ? normalized : fallback;
  } catch {
    window.localStorage.removeItem(storageKey);
    return fallback;
  }
}

const stageLabel = (s: MushroomStage) => {
  switch (s) {
    case "agar":
      return "Agar / Plate";
    case "liquid_culture":
      return "Liquid Culture";
    case "grain_spawn":
      return "Grain Spawn";
    case "bulk_spawn":
      return "Bulk Spawn";
    case "fruiting_block":
      return "Fruiting Block";
    default:
      return s;
  }
};

const statusLabel = (s: MushroomStatus) => {
  switch (s) {
    case "incubating":
      return "Incubating";
    case "casing":
      return "Casing";
    case "fruiting":
      return "Fruiting";
    case "cold_hold":
      return "Cold Hold";
    case "harvest":
      return "Harvest";
    case "completed":
      return "Completed";
    default:
      return s;
  }
};

const defaultTiming = {
  daysAgarPlate: 14,
  daysLiquidCulture: 10,
  daysGrainSpawn: 0,
  daysBulkSpawn: 0,
  daysFruitingBlockColonization: 14,
  daysCasing: 0,
  daysFruitingToFirstHarvest: 7,
  harvestWindowDays: 5,
};

const getTimingForStrainName = (strains: any[], name: string) => {
  const found = strains.find(
    (s) => (s?.name || "").toLowerCase() === (name || "").trim().toLowerCase(),
  );

  if (found) {
    return {
      daysAgarPlate:
        found.daysAgarPlate ??
        found.daysAgarToGrain ??
        defaultTiming.daysAgarPlate,
      daysLiquidCulture:
        found.daysLiquidCulture ??
        found.daysLCToGrain ??
        defaultTiming.daysLiquidCulture,
      daysGrainSpawn:
        found.daysGrainSpawn ??
        found.daysGrainToBulk ??
        defaultTiming.daysGrainSpawn,
      daysBulkSpawn: found.daysBulkSpawn ?? defaultTiming.daysBulkSpawn,
      daysFruitingBlockColonization:
        found.daysFruitingBlockColonization ??
        found.daysSpawnToBlock ??
        defaultTiming.daysFruitingBlockColonization,
      daysCasing: found.daysCasing ?? defaultTiming.daysCasing,
      daysFruitingToFirstHarvest:
        found.daysFruitingToFirstHarvest ??
        found.daysFruitingToHarvest ??
        defaultTiming.daysFruitingToFirstHarvest,
      harvestWindowDays:
        found.harvestWindowDays ?? defaultTiming.harvestWindowDays,
    };
  }

  return defaultTiming;
};

const estimateFirstHarvest = (
  strains: any[],
  strainName: string,
  stage: MushroomStage,
  inoculatedAt: string,
) => {
  const t = getTimingForStrainName(strains, strainName);

  const d = DateTime.fromFormat(inoculatedAt, "yyyy-LL-dd");
  if (!d.isValid)
    return { start: null as DateTime | null, end: null as DateTime | null };

  const daysFromStage =
    stage === "agar"
      ? t.daysAgarPlate +
        t.daysGrainSpawn +
        t.daysBulkSpawn +
        t.daysFruitingBlockColonization +
        t.daysCasing +
        t.daysFruitingToFirstHarvest
      : stage === "liquid_culture"
        ? t.daysLiquidCulture +
          t.daysGrainSpawn +
          t.daysBulkSpawn +
          t.daysFruitingBlockColonization +
          t.daysCasing +
          t.daysFruitingToFirstHarvest
        : stage === "grain_spawn"
          ? t.daysGrainSpawn +
            t.daysBulkSpawn +
            t.daysFruitingBlockColonization +
            t.daysCasing +
            t.daysFruitingToFirstHarvest
          : stage === "bulk_spawn"
            ? t.daysBulkSpawn +
              t.daysFruitingBlockColonization +
              t.daysCasing +
              t.daysFruitingToFirstHarvest
            : t.daysFruitingBlockColonization +
              t.daysCasing +
              t.daysFruitingToFirstHarvest;

  const start = d.plus({ days: daysFromStage });
  const end = start.plus({ days: t.harvestWindowDays });

  return { start, end };
};

const BoardDataGrid = React.memo(
  (props: {
    items: MushroomBoardItem[] | null;
    strains: any[];
    onSelectionChange: (params: GridRowSelectionModel) => void;
    initialPage?: number;
    onPaginationModelChange: (m: GridPaginationModel) => void;
    onClickRow: (id: GridRowId) => void;
    onChangeStatus: (id: number, next: MushroomStatus) => void;
  }) => {
    const csvFileName = React.useMemo(() => {
      const now = DateTime.local();
      return `SeedLeaf Mushroom Board - ${now.toFormat("MMM dd, yyyy")}`;
    }, []);

    const apiRef = useGridApiRef();
    const [initialState, setInitialState] = React.useState<GridInitialState>();

    const [paginationModel, setPaginationModel] =
      React.useState<GridPaginationModel>(() => {
        const raw = localStorage.getItem(boardGridStateStorageKey);
        if (raw) {
          try {
            const parsed = JSON.parse(raw);
            const saved = parsed?.pagination?.paginationModel;
            if (saved?.pageSize) {
              return {
                page: props.initialPage ?? saved.page ?? 0,
                pageSize: saved.pageSize,
              };
            }
          } catch {}
        }
        return {
          page: props.initialPage ?? 0,
          pageSize: 15,
        };
      });

    const saveSnapshot = React.useCallback(() => {
      if (apiRef?.current?.exportState) {
        const currentState = apiRef.current.exportState();

        if (!(currentState as any)?.pagination?.paginationModel?.pageSize) {
          (currentState as any).pagination = {
            paginationModel: { page: 0, pageSize: 25 },
          };
        }

        localStorage.setItem(
          boardGridStateStorageKey,
          JSON.stringify(currentState),
        );
      }
    }, [apiRef]);

    React.useLayoutEffect(() => {
      const raw = localStorage?.getItem(boardGridStateStorageKey);
      let parsed: GridInitialState | undefined;

      if (raw) {
        try {
          parsed = JSON.parse(raw);
        } catch {
          localStorage.removeItem(boardGridStateStorageKey);
        }
      }

      setInitialState({
        ...parsed,
        columns: {
          columnVisibilityModel: {
            notes: false,
            ...(parsed?.columns?.columnVisibilityModel ?? {}),
          },
          orderedFields: [],
        },
        filter: undefined,
        sorting: parsed?.sorting ?? {
          sortModel: [{ field: "inoculatedAt", sort: "desc" }],
        },
        pagination: {
          paginationModel: {
            page:
              (parsed as any)?.pagination?.paginationModel?.page ??
              props.initialPage ??
              0,
            pageSize:
              (parsed as any)?.pagination?.paginationModel?.pageSize ?? 15,
          },
        },
      });

      window.addEventListener("beforeunload", saveSnapshot);

      return () => {
        window.removeEventListener("beforeunload", saveSnapshot);
        saveSnapshot();
      };
    }, [saveSnapshot, props.initialPage]);

    const [density, setDensity] = React.useState<GridDensity>(() => {
      const d = localStorage.getItem(
        boardGridDensityStorageKey,
      ) as GridDensity | null;
      return d === "compact" || d === "comfortable" || d === "standard"
        ? d
        : "standard";
    });

    const columns: GridColDef[] = [
      {
        ...GRID_CHECKBOX_SELECTION_COL_DEF,
        hideable: false,
        headerName: "Checkbox",
      },
      {
        field: "archived",
        headerName: "",
        width: 35,
        minWidth: 35,
        sortable: false,
        filterable: false,
        align: "center",
        disableColumnMenu: true,
        disableExport: true,
        renderCell: (params) =>
          params.row.archivedAt ? (
            <Inventory2OutlinedIcon fontSize="small" titleAccess="Archived" />
          ) : null,
      },
      {
        field: "code",
        headerName: "Code",
        width: 140,
      },
      {
        field: "strainName",
        headerName: "Strain",
        width: 220,
      },
      {
        field: "stage",
        headerName: "Stage",
        width: 170,
        renderCell: (params) => stageLabel(params.row.stage),
      },
      { field: "productionUnitName", headerName: "Unit", width: 180 },
      { field: "unitCount", headerName: "Qty", width: 90 },
      {
        field: "status",
        headerName: "Status",
        width: 170,
        sortable: false,
        renderCell: (params) => (
          <TextField
            value={params.row.status}
            onChange={(e) =>
              props.onChangeStatus(
                Number(params.row.id),
                e.target.value as MushroomStatus,
              )
            }
            select
            size="small"
            fullWidth
          >
            <MenuItem value="incubating">Incubating</MenuItem>
            <MenuItem value="casing">Casing</MenuItem>
            <MenuItem value="fruiting">Fruiting</MenuItem>
            <MenuItem value="cold_hold">Cold Hold</MenuItem>
            <MenuItem value="harvest">Harvest</MenuItem>
            <MenuItem value="completed">Completed</MenuItem>
          </TextField>
        ),
      },
      {
        field: "roomName",
        headerName: "Room",
        width: 200,
      },
      {
        field: "inoculatedAt",
        headerName: "Inoculated",
        width: 160,
      },
      {
        field: "estimated",
        headerName: "Est. First Harvest",
        width: 190,
        sortable: false,
        valueGetter: (_: any, row: any) => {
          const est = estimateFirstHarvest(
            props.strains,
            row.strainName,
            row.stage,
            row.inoculatedAt,
          );

          if (!est.start) return "";

          return `${est.start.toFormat("MMM dd")} - ${est.end?.toFormat("MMM dd")}`;
        },
      },
      {
        field: "notes",
        headerName: "Notes",
        width: 300,
      },
    ];

    if (!props.items) return null;

    if (!initialState) {
      return <CircularProgress />;
    }

    return (
      <StripedDataGrid
        apiRef={apiRef}
        columns={columns}
        rows={props.items.map((r) => ({
          id: r.id,
          code: r.code,
          strainName: r.strainName,
          stage: r.stage,
          status: r.status,
          roomName: r.roomName,
          inoculatedAt: r.inoculatedAt,
          notes: r.notes,
          archivedAt: r.archivedAt,
          productionUnitName: r.productionUnitName,
          unitCount: r.unitCount,
        }))}
        initialState={{
          ...initialState,
          filter: undefined,
          sorting: {
            sortModel: [
              {
                field: "inoculatedAt",
                sort: "desc",
              },
            ],
          },
        }}
        checkboxSelection
        onRowSelectionModelChange={props.onSelectionChange}
        pagination
        paginationMode="client"
        pageSizeOptions={[15, 25, 50, 100]}
        paginationModel={paginationModel}
        onPaginationModelChange={(m) => {
          setPaginationModel(m);
          props.onPaginationModelChange(m);
        }}
        showToolbar
        slots={{
          toolbar: GridToolbar,
        }}
        density={density}
        onDensityChange={(d) => {
          setDensity(d);
          localStorage.setItem(boardGridDensityStorageKey, d);
        }}
        slotProps={{
          toolbar: {
            showQuickFilter: true,
            csvOptions: {
              allColumns: true,
              fileName: csvFileName,
            },
          },
        }}
        getRowClassName={(params: GridRowClassNameParams) => {
          const row = params.row as any;
          if (row.archivedAt) {
            return "archived-row";
          }
          return params.indexRelativeToCurrentPage % 2 === 0 ? "even" : "odd";
        }}
        disableRowSelectionOnClick
        onRowClick={(params: GridRowParams) => {
          props.onClickRow(params.id);
        }}
      />
    );
  },
);

const getNextBoardCode = (items: MushroomBoardItem[] | null) => {
  const prefix = "SUB-";

  if (!items || items.length === 0) {
    return `${prefix}0001`;
  }

  let max = 0;

  for (const it of items) {
    const code = String(it.code || "");
    if (!code.startsWith(prefix)) continue;

    const n = parseInt(code.replace(prefix, ""), 10);
    if (!isNaN(n)) {
      max = Math.max(max, n);
    }
  }

  const next = max + 1;

  return `${prefix}${String(next).padStart(4, "0")}`;
};

export default function MushroomBoardPage() {
  const [items, setItems] = useState<MushroomBoardItem[] | null>(null);
  const [selected, setSelected] = useState<number[]>([]);
  const [busy, setBusy] = useState(false);

  const { handleError } = useContext(ErrorContext);

  const [addOpen, setAddOpen] = useState(false);
  const [addCode, setAddCode] = useState("");
  const [addStrainName, setAddStrainName] = useState("");
  const [addStage, setAddStage] = useState<StageValue>("");
  const [addStatus, setAddStatus] = useState<StatusValue>("");
  const [addRoomName, setAddRoomName] = useState("");
  const [addInoculatedAt, setAddInoculatedAt] = useState(
    DateTime.local().toFormat("yyyy-LL-dd"),
  );
  const [addNotes, setAddNotes] = useState("");

  const showArchivedCheckedStorageKey = "mushroomBoardShowArchivedChecked";
  const showActiveCheckedStorageKey = "mushroomBoardShowActiveChecked";
  const [showArchivedChecked, setShowArchivedChecked] = useState(
    window.localStorage.getItem(showArchivedCheckedStorageKey) !==
      false.toString(),
  );
  const [showActiveChecked, setShowActiveChecked] = useState(
    window.localStorage.getItem(showActiveCheckedStorageKey) !==
      false.toString(),
  );

  const [addProductionUnitId, setAddProductionUnitId] = useState<string>("");
  const [addUnitCount, setAddUnitCount] = useState<string>("1");
  const [strains, setStrains] = useState<any[]>([]);
  const [rooms, setRooms] = useState<any[]>([]);
  const [productionUnits, setProductionUnits] = useState<
    ProductionUnitOption[]
  >([]);

  const history = useHistory();
  let initialPage = getSearchParamNumber(history, "page", 0);

  useEffect(() => {
    const raw = window.localStorage.getItem(mushroomProductionUnitsStorageKey);
    if (!raw) {
      setProductionUnits(defaultProductionUnits);
      return;
    }

    try {
      const parsed = JSON.parse(raw);
      const list = Array.isArray(parsed) ? parsed : [];

      const normalized = list
        .map((x: any) => ({
          id: Number(x?.id ?? x?.Id),
          name: (x?.name ?? x?.Name ?? "").trim(),
          wetWeight: x?.wetWeight ?? x?.WetWeight ?? null,
          wetWeightUom: x?.wetWeightUom ?? x?.WetWeightUom ?? null,
        }))
        .filter((x: any) => !!x.id && !!x.name);

      setProductionUnits(
        normalized.length ? normalized : defaultProductionUnits,
      );
    } catch {
      window.localStorage.removeItem(mushroomProductionUnitsStorageKey);
      setProductionUnits(defaultProductionUnits);
    }
  }, []);

  useEffect(() => {
    setStrains(
      loadStrainsWithFallback(mushroomStrainsStorageKey, defaultStrains),
    );
  }, []);

  useEffect(() => {
    setRooms(loadOptionsWithFallback(mushroomRoomsStorageKey, defaultRooms));
  }, []);

  useEffect(() => {
    try {
      const raw = window.localStorage.getItem(mushroomBoardStorageKey);

      if (raw) {
        const parsed = JSON.parse(raw) as MushroomBoardItem[];
        setItems(parsed);
        return;
      }
    } catch {
      window.localStorage.removeItem(mushroomBoardStorageKey);
    }

    const defaults: MushroomBoardItem[] = [
      {
        id: 1,
        code: "SUB-0001",
        strainName: "Blue Oyster",
        stage: "fruiting_block",
        status: "incubating",
        roomName: "Incubation Shelf 1",
        inoculatedAt: DateTime.local()
          .minus({ days: 2 })
          .toFormat("yyyy-LL-dd"),
        notes: null,
        archivedAt: null,
        productionUnitId: 1,
        productionUnitName: "10 lb Bag",
        unitCount: 24,
      },

      {
        id: 2,
        code: "SUB-0002",
        strainName: "Lion’s Mane",
        stage: "fruiting_block",
        status: "incubating",
        roomName: "Incubation Shelf 1",
        inoculatedAt: DateTime.local()
          .minus({ days: 6 })
          .toFormat("yyyy-LL-dd"),
        notes: null,
        archivedAt: null,
        productionUnitId: 1,
        productionUnitName: "10 lb Bag",
        unitCount: 24,
      },
      {
        id: 3,
        code: "SUB-0003",
        strainName: "Blue Oyster",
        stage: "fruiting_block",
        status: "fruiting",
        roomName: "Fruiting Room A",
        inoculatedAt: DateTime.local()
          .minus({ days: 18 })
          .toFormat("yyyy-LL-dd"),
        notes: null,
        archivedAt: null,
        productionUnitId: 1,
        productionUnitName: "10 lb Bag",
        unitCount: 24,
      },
    ];

    setItems(defaults);
    window.localStorage.setItem(
      mushroomBoardStorageKey,
      JSON.stringify(defaults),
    );
  }, []);

  const handleSelectionChange = (m: GridRowSelectionModel) => {
    let ids: number[] = [];
    const anyM = m as any;
    if (Array.isArray(m)) ids = m.map((v) => Number(v));
    else if (anyM?.ids?.forEach) {
      const t: number[] = [];
      anyM.ids.forEach((v: unknown) => t.push(Number(v)));
      ids = t;
    }
    setSelected(ids.length ? [ids[0]] : []);
  };

  const handleShowActiveChanged = (checked: boolean) => {
    setShowActiveChecked(checked);
    window.localStorage.setItem(
      showActiveCheckedStorageKey,
      checked.toString(),
    );
  };

  const handleShowArchivedChanged = (checked: boolean) => {
    setShowArchivedChecked(checked);
    window.localStorage.setItem(
      showArchivedCheckedStorageKey,
      checked.toString(),
    );
  };

  const onClickRow = (id: GridRowId) => {
    return;
  };

  const needsUnits = addStage === "bulk_spawn" || addStage === "fruiting_block";

  const handleClickAdd = () => {
    setAddCode(getNextBoardCode(items));
    setAddStrainName("");
    setAddRoomName("");
    setAddStage("");
    setAddStatus("");
    setAddInoculatedAt(DateTime.local().toFormat("yyyy-LL-dd"));
    setAddNotes("");
    setAddOpen(true);
    setAddProductionUnitId("");
    setAddUnitCount("1");
  };

  const handleCloseAdd = () => {
    setAddOpen(false);
  };

  const handleSaveAdd = () => {
    if (!items) return;
    if (!addCode.trim()) return;

    if (!addStrainName.trim()) return;
    if (!addRoomName.trim()) return;
    if (!addStage) return;
    if (!addStatus) return;
    if (needsUnits) {
      if (!addProductionUnitId) return;
      if (!(Number(addUnitCount) > 0)) return;
    }

    setBusy(true);

    try {
      const maxId = items.reduce((m, v) => Math.max(m, v.id), 0);
      const unit = productionUnits.find(
        (u) => u.id === Number(addProductionUnitId),
      );

      const next: MushroomBoardItem = {
        id: maxId + 1,
        code: addCode.trim(),
        strainName: addStrainName.trim() ? addStrainName.trim() : "Unknown",
        stage: addStage,
        status: addStatus,
        roomName: addRoomName.trim() ? addRoomName.trim() : null,
        inoculatedAt: addInoculatedAt,
        notes: addNotes.trim() ? addNotes.trim() : null,
        archivedAt: null,
        productionUnitId: needsUnits ? Number(addProductionUnitId) : null,
        productionUnitName: needsUnits ? (unit?.name ?? null) : null,
        unitCount: needsUnits ? Number(addUnitCount) : null,
      };

      const nextItems = [next, ...items];

      setItems(nextItems);
      window.localStorage.setItem(
        mushroomBoardStorageKey,
        JSON.stringify(nextItems),
      );

      setAddOpen(false);
    } catch (error) {
      handleError(error);
    } finally {
      setBusy(false);
    }
  };

  const handleChangeStatus = (id: number, next: MushroomStatus) => {
    if (!items) return;

    try {
      const nextItems = items.map((v) =>
        v.id === id ? { ...v, status: next } : v,
      );
      setItems(nextItems);
      window.localStorage.setItem(
        mushroomBoardStorageKey,
        JSON.stringify(nextItems),
      );
    } catch (error) {
      handleError(error);
    }
  };

  return (
    <Page title="Production Board">
      {items && (
        <>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              mb: 2,
              gap: 1,
              flexWrap: "wrap",
            }}
          >
            <Button
              variant="contained"
              color="primary"
              onClick={handleClickAdd}
              disabled={busy}
            >
              Start New Production
            </Button>

            <IconButton
              aria-label="delete"
              disabled={busy || selected?.length === 0}
              onClick={() => {
                return;
              }}
              size="large"
            >
              <DeleteIcon />
            </IconButton>

            <ActiveFilterControls
              showActiveChecked={showActiveChecked}
              showArchivedChecked={showArchivedChecked}
              onChangeShowActive={handleShowActiveChanged}
              onChangeShowArchived={handleShowArchivedChanged}
            />
          </Box>

          <Box sx={{ height: 750, width: "100%", pt: 2 }}>
            <BoardDataGrid
              items={items.filter(
                (item) =>
                  (showActiveChecked && item.archivedAt === null) ||
                  (showArchivedChecked && item.archivedAt !== null),
              )}
              strains={strains}
              onSelectionChange={handleSelectionChange}
              initialPage={initialPage}
              onPaginationModelChange={(m) => {
                const params = new URLSearchParams(`page=${m.page}`);
                history.replace({
                  search: params.toString(),
                });
              }}
              onClickRow={onClickRow}
              onChangeStatus={handleChangeStatus}
            />
          </Box>
        </>
      )}

      <Dialog open={addOpen} onClose={handleCloseAdd} fullWidth maxWidth="sm">
        <DialogTitle>Start New Production</DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 1, display: "flex", flexDirection: "column", gap: 2 }}>
            <TextField
              label="Code"
              value={addCode}
              onChange={(e) => setAddCode(e.target.value)}
              fullWidth
            />

            <TextField
              label="Strain"
              value={addStrainName}
              onChange={(e) => setAddStrainName(e.target.value)}
              select
              fullWidth
            >
              <MenuItem value="" disabled>
                Select a strain…
              </MenuItem>

              {strains.map((s) => (
                <MenuItem key={s.name} value={s.name}>
                  {s.name}
                </MenuItem>
              ))}
            </TextField>

            <TextField
              label="Stage"
              value={addStage}
              onChange={(e) => setAddStage(e.target.value as StageValue)}
              select
              fullWidth
            >
              <MenuItem value="" disabled>
                Select a stage…
              </MenuItem>
              <MenuItem value="agar">Agar / Plate</MenuItem>
              <MenuItem value="liquid_culture">Liquid Culture</MenuItem>
              <MenuItem value="grain_spawn">Grain Spawn</MenuItem>
              <MenuItem value="bulk_spawn">Bulk Spawn</MenuItem>
              <MenuItem value="fruiting_block">Fruiting Block</MenuItem>
            </TextField>

            {needsUnits && (
              <>
                <TextField
                  label="Production Unit"
                  value={addProductionUnitId}
                  onChange={(e) => setAddProductionUnitId(e.target.value)}
                  select
                  fullWidth
                >
                  <MenuItem value="" disabled>
                    Select a production unit…
                  </MenuItem>
                  {productionUnits.map((u) => (
                    <MenuItem key={u.id} value={String(u.id)}>
                      {u.name}
                    </MenuItem>
                  ))}
                </TextField>

                <TextField
                  label="Unit Count"
                  value={addUnitCount}
                  onChange={(e) => setAddUnitCount(e.target.value)}
                  type="number"
                  fullWidth
                  inputProps={{ min: 1 }}
                />
              </>
            )}

            <TextField
              label="Status"
              value={addStatus}
              onChange={(e) => setAddStatus(e.target.value as StatusValue)}
              select
              fullWidth
            >
              <MenuItem value="" disabled>
                Select a status…
              </MenuItem>
              <MenuItem value="incubating">Incubating</MenuItem>
              <MenuItem value="casing">Casing</MenuItem>
              <MenuItem value="fruiting">Fruiting</MenuItem>
              <MenuItem value="cold_hold">Cold Hold</MenuItem>
              <MenuItem value="harvest">Harvest</MenuItem>
              <MenuItem value="completed">Completed</MenuItem>
            </TextField>

            <TextField
              label="Room"
              value={addRoomName}
              onChange={(e) => setAddRoomName(e.target.value)}
              select
              fullWidth
            >
              {rooms.length === 0 ? (
                <MenuItem value="" disabled>
                  Select a room…
                </MenuItem>
              ) : (
                rooms.map((r) => (
                  <MenuItem key={r.name} value={r.name}>
                    {r.name}
                  </MenuItem>
                ))
              )}
            </TextField>

            <TextField
              label="Inoculated Date"
              type="date"
              value={addInoculatedAt}
              onChange={(e) => setAddInoculatedAt(e.target.value)}
              fullWidth
              InputLabelProps={{ shrink: true }}
            />

            <TextField
              label="Notes"
              value={addNotes}
              onChange={(e) => setAddNotes(e.target.value)}
              fullWidth
              multiline
              minRows={3}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseAdd} disabled={busy}>
            Cancel
          </Button>
          <Button
            onClick={handleSaveAdd}
            color="primary"
            variant="contained"
            disabled={
              busy ||
              !addCode.trim() ||
              !addStrainName.trim() ||
              !addRoomName.trim() ||
              !addStage ||
              !addStatus
            }
          >
            Save
          </Button>
        </DialogActions>
      </Dialog>

      <LoadingBackdrop open={busy} />
    </Page>
  );
}
